/**
 * Loads a module from a webpack 5 Module Federation remote whose URL is only
 * known at runtime.
 *
 * `webpack.config.js` deliberately declares no `remotes`, because the URLs are
 * per-environment config (`env.json`) rather than build-time constants. That
 * means doing by hand what a static `remotes` entry would have generated — the
 * three-step container handshake, which is webpack's own protocol:
 *
 *   1. `__webpack_init_sharing__('default')` — ensure the host's share scope is
 *      initialised before anything reads it.
 *   2. `container.init(shareScope)` — hand the remote the host's copies of React,
 *      Emotion and MUI (see `webpack.shared.js`) so it consumes those rather than
 *      loading a second instance of each.
 *   3. `container.get(module)` — resolve an exposed module to a factory.
 *
 * Both globals are webpack runtime builtins, not real imports; they only exist in
 * a bundle built by webpack with `ModuleFederationPlugin` present.
 */
/** Keyed by URL so concurrent callers share one `<script>` and one container. */
const containers = new Map();
function scriptFor(url, globalName) {
    return new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.src = url;
        el.async = true;
        el.onload = () => {
            const container = window[globalName];
            if (container && typeof container.get === 'function') {
                resolve(container);
            }
            else {
                reject(new Error(`${url} loaded but did not expose container "${globalName}"`));
            }
        };
        el.onerror = () => reject(new Error(`Failed to load remote entry ${url}`));
        document.head.appendChild(el);
    });
}
/**
 * `container.init` throws if called twice on the same container, so initialising
 * is part of the cached promise rather than something each caller repeats.
 */
function loadContainer(url, globalName) {
    const cached = containers.get(url);
    if (cached)
        return cached;
    const pending = (async () => {
        await __webpack_init_sharing__('default');
        const container = await scriptFor(url, globalName);
        await container.init(__webpack_share_scopes__.default);
        return container;
    })().catch((err) => {
        /* Drop the failed attempt so a later call can retry after a transient error. */
        containers.delete(url);
        throw err;
    });
    containers.set(url, pending);
    return pending;
}
export async function loadRemoteModule(request) {
    if (typeof document === 'undefined')
        throw new Error('No document');
    const container = await loadContainer(request.url, request.globalName);
    const factory = await container.get(request.module);
    return factory();
}
